
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const PublishDQRImages = () => {
  // State variables
  const navigate = useNavigate();
  const [deviceStatus, setDeviceStatus] = useState('Not connected');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedOption, setSelectedOption] = useState('0x10');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSelectOptions, setShowSelectOptions] = useState(true);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dqrData, setDqrData] = useState(null);
  const [imageToUpload, setImageToUpload] = useState(null);
  const [isProcessingAllImages, setIsProcessingAllImages] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImagesProgress, setAllImagesProgress] = useState(0);
  const [showLogs, setShowLogs] = useState(false);

  // Refs
  const logContainerRef = useRef(null);
  const deviceRef = useRef(null);
  
  // Protocol constants
  const m_report_len = 65;
  const m_gNumberRef = useRef(0);

  // Screen type mapping
  const screenTypes = {
    '0x10': { name: 'DQR', category: 'QR' },
    '0x11': { name: 'Success screen', category: 'SUCCESS' },
    '0x12': { name: 'Fail screen', category: 'FAIL' },
    '0x13': { name: 'Pending screen', category: 'PENDING' },
    '0x14': { name: 'Default page', category: 'WELCOME' }
  };

  const fetchDQRData = async () => {
  try {
    setLoading(true);
    const response = await axios.get(`https://api.dqr-display-tms.bonrix.in/api/Share/DQR/${id}`); 
    
    // Check for custom error message
    if (response.data?.errormsg === "No matching records found") {
      navigate("/not-found"); // Redirect to 404 page
      return;
    }

    setDqrData(response.data);

    if (response.data?.data?.categorydata?.length > 0) {
      const welcomeCategory = response.data.data.categorydata.find(
        cat => cat.categoryname.toUpperCase() === 'WELCOME'
      );
      if (welcomeCategory) {
        setSelectedCategory(welcomeCategory);
        if (welcomeCategory.imagedata?.length > 0) {
          setImageToUpload(welcomeCategory.imagedata[0]);
          addLog(`Default image selected: ${welcomeCategory.imagedata[0].name}`);
        }
      } else {
        setSelectedCategory(response.data.data.categorydata[0]);
        if (response.data.data.categorydata[0].imagedata?.length > 0) {
          setImageToUpload(response.data.data.categorydata[0].imagedata[0]);
          addLog(`Default image selected: ${response.data.data.categorydata[0].imagedata[0].name}`);
        }
      }
    }
    setError(null);
  } catch (err) {
    console.error('Error fetching DQR data:', err);
    setError('Failed to load DQR data. Please check the ID and try again.');
    navigate("/not-found"); // Optional: also redirect on network errors
  } finally {
    setLoading(false);
  }
};
  
  const updateSelectedCategoryFromOption = (option) => {
    if (!dqrData?.data?.categorydata) return;
    
    const categoryType = screenTypes[option]?.category || 'WELCOME';
    const category = dqrData.data.categorydata.find(cat => 
      cat.categoryname.toUpperCase() === categoryType
    );
    
    if (category) {
      setSelectedCategory(category);
      if (category.imagedata?.length > 0) {
        setImageToUpload(category.imagedata[0]);
        addLog(`Selected image: ${category.imagedata[0].name}`);
      } else {
        setImageToUpload(null);
        addLog(`No images found in ${categoryType} category`);
      }
    } else {
      const welcomeCategory = dqrData.data.categorydata.find(cat => 
        cat.categoryname.toUpperCase() === 'WELCOME'
      );
      
      if (welcomeCategory) {
        setSelectedCategory(welcomeCategory);
        if (welcomeCategory.imagedata?.length > 0) {
          setImageToUpload(welcomeCategory.imagedata[0]);
          addLog(`Defaulting to WELCOME category image: ${welcomeCategory.imagedata[0].name}`);
        } else {
          setImageToUpload(null);
          addLog('No images found in WELCOME category');
        }
      } else {
        setSelectedCategory(null);
        setImageToUpload(null);
        addLog(`Category ${categoryType} not found and no WELCOME category available`);
      }
    }
  };
  
  useEffect(() => {
    if (id) {
      fetchDQRData();
    } else {
      setError('No DQR ID provided');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!('hid' in navigator)) {
      addLog('WebHID is not supported in this browser');
    }
  }, []);

  // Fix: Added useEffect to handle scrolling after logs update
  useEffect(() => {
    // Scroll to the bottom of the log container whenever logs change
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const addLog = (logMessage) => {
    setLogs(prevLogs => [...prevLogs, logMessage]);
    // Remove the scrolling logic from here since we're now handling it in the useEffect
  };

  const bufferToHexString = (buffer) => {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
  };

  const lrc = (data) => {
    let cLRC = 0;
    for (let i = 0; i < data.length; i++) {
      cLRC ^= data[i];
    }
    return cLRC;
  };

  const LRC = (cLRC, data) => {
    for (let i = 0; i < data.length; i++) {
      cLRC ^= data[i];
    }
    return cLRC;
  };

  const IsIllegalCMD = (cmd, buff) => {
    console.log(`response cmd: ${bufferToHexString(buff)}`);
    if (buff[0] !== cmd[0] || buff[1] !== cmd[1]) {
      const err = `request cmd [${cmd[0].toString(16).padStart(2, '0').toUpperCase()}${cmd[1].toString(16).padStart(2, '0').toUpperCase()}] is not equal to response cmd [${buff[0].toString(16).padStart(2, '0').toUpperCase()}${buff[1].toString(16).padStart(2, '0').toUpperCase()}]`;
      return { valid: false, error: err };
    }

    if (buff[2] === 0x00 && buff[3] === 0x00) {
      return { valid: true, error: "" };
    }

    const err = `cmd return fail [${buff[2].toString(16).padStart(2, '0').toUpperCase()}${buff[3].toString(16).padStart(2, '0').toUpperCase()}]`;
    return { valid: false, error: err };
  };

  const cs_send = async (cmd, data, dataLen, outLRC) => {
    if (!deviceRef.current) { 
      addLog('Device not connected'); 
      return -1; 
    }

    const cmdData = new Uint8Array(1024 * 10);
    cmdData[2] = 0x02;
    cmdData[3] = m_gNumberRef.current;
    cmdData[4] = cmd[0];
    cmdData[5] = cmd[1];

    const bufflen = m_report_len - 9;
    cmdData[6] = dataLen >> 8;
    cmdData[7] = dataLen & 0xFF;

    let reallen = dataLen;
    if (dataLen > 0) {
      if (dataLen > bufflen) {
        cmdData.set(data.subarray(0, bufflen + 1), 8);
        reallen = bufflen + 1;
      } else {
        cmdData.set(data.subarray(0, dataLen), 8);
      }
    }

    m_gNumberRef.current = (m_gNumberRef.current === 0xFF ? 0x01 : m_gNumberRef.current + 1);

    let length, cLRC;
    if (dataLen > bufflen) {
      length = 6 + reallen;
      cLRC = lrc(cmdData.subarray(2, 6 + reallen));
      outLRC[0] = cLRC;
    } else {
      cLRC = lrc(cmdData.subarray(2, 6 + reallen));
      cmdData[8 + reallen] = cLRC;
      length = 7 + reallen;
    }

    cmdData[0] = length >> 8;
    cmdData[1] = length & 0xFF;

    length = length + 2;

    if (length > m_report_len) {
      addLog('❌ packet larger than report size');
      return -1;
    }

    try {
      const res = await deviceRef.current.sendReport(0, cmdData.slice(1, length));
      if (res < 0) {
        return res;
      }
      return res;
    } catch (error) {
      addLog(`Send error: ${error.message}`);
      return -1;
    }
  };

  const cs_read = async (timeout_ms) => {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        resolve({ length: 0, data: new Uint8Array(0) });
      }, timeout_ms);

      deviceRef.current.addEventListener('inputreport', function onInputReport(event) {
        clearTimeout(timeoutId);
        deviceRef.current.removeEventListener('inputreport', onInputReport);

        const data = new Uint8Array(event.data.buffer);
        addLog(`Received: ${bufferToHexString(data)}`);

        if (data.length === 0) {
          resolve({ length: -99, data: new Uint8Array(0) });
          return;
        }

        const len_byte = data[0];

        if (len_byte < 7) {
          resolve({ length: -1, data: new Uint8Array(0) });
          return;
        }

        if (data[1] !== 0x02) {
          resolve({ length: -2, data: new Uint8Array(0) });
          return;
        }

        const cmdtype = data[3];
        const cmd = data[4];
        const datalen = (data[5] << 8) | data[6];

        if (datalen > 0 && datalen === len_byte - 7) {
          const resultData = new Uint8Array(datalen + 2);
          resultData[0] = cmdtype;
          resultData[1] = cmd;
          resultData.set(data.slice(7, 7 + datalen), 2);
          resolve({ length: datalen + 2, data: resultData });
        } else {
          resolve({ length: -3, data: new Uint8Array(0) });
        }
      }, { once: true });
    });
  };

  const downloadAndProcessImage = async (imageUrl, cmd) => {
    try {
      addLog(`Downloading image from: ${imageUrl}`);
      
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      const fileBuffer = await response.arrayBuffer();
      const fileData = new Uint8Array(fileBuffer);

      const filelen = fileData.length;
      let rlen = filelen;
      const bufflen = m_report_len - 9 - 12;
      let startAddr = 0;

      addLog(`File size: ${filelen} bytes`);

      const pt = new Uint8Array(1024);

      while (rlen > 0) {
        const len = Math.min(rlen, 4 * 1024);
        const buff = fileData.slice(startAddr, startAddr + len);

        pt[0] = (startAddr >> 24) & 0xFF;
        pt[1] = (startAddr >> 16) & 0xFF;
        pt[2] = (startAddr >> 8) & 0xFF;
        pt[3] = startAddr & 0xFF;

        pt[4] = (len >> 24) & 0xFF;
        pt[5] = (len >> 16) & 0xFF;
        pt[6] = (len >> 8) & 0xFF;
        pt[7] = len & 0xFF;

        pt[8] = (filelen >> 24) & 0xFF;
        pt[9] = (filelen >> 16) & 0xFF;
        pt[10] = (filelen >> 8) & 0xFF;
        pt[11] = filelen & 0xFF;

        if (len <= bufflen) {
          pt.set(buff, 12);
          const datalen = 12 + len;
          const outLRC = new Uint8Array(1);
          const res = await cs_send(cmd, pt.slice(0, datalen), datalen, outLRC);
          if (res <= 0) {
            throw new Error("Failed to send data.");
          }
        } else {
          pt.set(buff.slice(0, bufflen + 1), 12);
          const datalen = 12 + len;
          const outLRC = new Uint8Array(1);
          const res = await cs_send(cmd, pt.slice(0, datalen), datalen, outLRC);
          if (res <= 0) {
            throw new Error("Failed to send data.");
          }

          let packlen = len;
          let pCur = bufflen + 1;
          packlen = packlen - bufflen - 1;
          let curlen = 0;
          let sendlen = 0;
          let bSendLRC = false;
          let cLRC = 0;

          while (packlen > 0) {
            curlen = Math.min(packlen, m_report_len - 2);
            const tempBuff = buff.slice(pCur, pCur + curlen);

            cLRC = LRC(cLRC, tempBuff);

            if (curlen < m_report_len - 2) {
              pt[0] = ((curlen + 1) >> 8) & 0xFF;
              pt[1] = (curlen + 1) & 0xFF;
              pt.set(tempBuff, 2);
              pt[2 + curlen] = cLRC;
              sendlen = 3 + curlen;
              bSendLRC = true;
            } else {
              pt[0] = (curlen >> 8) & 0xFF;
              pt[1] = curlen & 0xFF;
              pt.set(tempBuff, 2);
              sendlen = 2 + curlen;
            }

            addLog(`[SEND cs_send] ${bufferToHexString(pt.slice(0, sendlen))}`);
            await deviceRef.current.sendReport(0, pt.slice(1, sendlen));

            pCur = pCur + curlen;
            packlen = packlen - curlen;
          }

          if (!bSendLRC) {
            pt[0] = (1 >> 8) & 0xFF;
            pt[1] = 1 & 0xFF;
            pt[2] = cLRC;

            addLog(`Sending LRC: ${bufferToHexString(pt.slice(0, 3))}`);
            const ret = await deviceRef.current.sendReport(0, pt.slice(0, 3));
            if (ret <= 0) {
              throw new Error("Download failed");
            }
          }
        }

        const { length: ret, data: readBuff } = await cs_read(3000);
        if (ret <= 0) {
          throw new Error("Download failed - Read timeout");
        }

        const { valid: iicValid, error: iicErr } = IsIllegalCMD(cmd, readBuff);
        if (!iicValid) {
          addLog(`Error: ${iicErr}`);
        }

        rlen = rlen - len;
        startAddr = startAddr + len;

        const currentProgress = Math.floor((startAddr / filelen) * 100);
        setProgress(currentProgress);
        addLog(`Progress: ${startAddr}/${filelen} bytes sent (${currentProgress}%)`);
      }

      addLog("Image upload completed!");
      return true;
    } catch (error) {
      addLog(`Error: ${error.message}`);
      return false;
    }
  };

  const handleOptionChange = (e) => {
    const newOption = e.target.value;
    setSelectedOption(newOption);
    updateSelectedCategoryFromOption(newOption);
  };

  const handleConnect = async () => {
    try {
      const devices = await navigator.hid.requestDevice({
        filters: [{ vendorId: 0x2C7C, productId: 0x0901 }]
      });

      if (devices.length === 0) {
        addLog('No device selected');
        return;
      }

      deviceRef.current = devices[0];
      await deviceRef.current.open();

      setDeviceStatus(`Connected: ${deviceRef.current.productName}`);
      setIsConnected(true);
      addLog(`Connected to ${deviceRef.current.productName}`);
    } catch (error) {
      addLog(`Error connecting to device: ${error.message}`);
    }
  };

  const handleUpload = async () => {
    if (!deviceRef.current) {
      addLog('Please connect to a device first');
      return;
    }

    if (!imageToUpload) {
      addLog('No image available to upload');
      return;
    }

    setProgress(0);

    const optionValue = parseInt(selectedOption, 16);
    
    addLog(`Selected option: ${screenTypes[selectedOption].name} (${selectedOption})`);
    addLog(`Selected image: ${imageToUpload.name} (${imageToUpload.filepath})`);

    const cmd = new Uint8Array([0x03, optionValue]);

    await downloadAndProcessImage(imageToUpload.filepath, cmd);
  };
  
  const handleApplyAllImages = async () => {
    if (!deviceRef.current) {
      addLog('Please connect to a device first');
      return;
    }
    
    if (!dqrData?.data?.categorydata) {
      addLog('No DQR data available');
      return;
    }
    
    setShowSelectOptions(false);
    setIsProcessingAllImages(true);
    setCurrentImageIndex(0);
    setAllImagesProgress(0);
    
    const categories = dqrData.data.categorydata;
    const totalImages = Object.keys(screenTypes).length;
    let successCount = 0;
    
    addLog('Starting to apply all images...');
    
    for (let i = 0; i < Object.keys(screenTypes).length; i++) {
      const option = Object.keys(screenTypes)[i];
      const { category } = screenTypes[option];
      
      setCurrentImageIndex(i + 1);
      setAllImagesProgress(Math.floor((i / totalImages) * 100));
      
      const matchingCategory = categories.find(cat => 
        cat.categoryname.toUpperCase() === category
      );
      
      if (matchingCategory?.imagedata?.length > 0) {
        const image = matchingCategory.imagedata[0];
        addLog(`Applying ${screenTypes[option].name} image: ${image.name} (${option})`);
        
        const cmd = new Uint8Array([0x03, parseInt(option, 16)]);
        setProgress(0);
        const success = await downloadAndProcessImage(image.filepath, cmd);
        
        if (success) {
          successCount++;
          addLog(`Successfully applied ${screenTypes[option].name} image (${i + 1}/${totalImages})`);
        } else {
          addLog(`Failed to apply ${screenTypes[option].name} image`);
        }
        
        setAllImagesProgress(Math.floor(((i + 1) / totalImages) * 100));
        
        if (i < Object.keys(screenTypes).length - 1) {
          addLog('Waiting before next image upload...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } else {
        addLog(`No image found for ${screenTypes[option].name}, skipping...`);
        setCurrentImageIndex(i + 1);
        setAllImagesProgress(Math.floor(((i + 1) / totalImages) * 100));
      }
    }
    
    setIsProcessingAllImages(false);
    setShowSelectOptions(true);
    addLog(`Completed applying all images. Success: ${successCount}/${totalImages}`);
  };

  const getCategoryImages = () => {
    if (!selectedCategory?.imagedata?.length) return [];
    return selectedCategory.imagedata;
  };

  // Styles
  const styles = {
    container: {
      fontFamily: "'Segoe UI', Arial, sans-serif",
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: 'var(--bg)',
      minHeight: '100vh',
      boxSizing: 'border-box',
      color: 'var(--text)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px 20px',
      backgroundColor: 'var(--surface)',
      color: 'var(--text)',
      borderRadius: '12px',
      marginBottom: '25px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    },
    title: {
      fontSize: '22px',
      fontWeight: '600',
      margin: '0',
      letterSpacing: '0.5px'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontWeight: '600',
      fontSize: '14px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    statusConnected: {
      backgroundColor: 'var(--success)',
      color: '#fff'
    },
    statusDisconnected: {
      backgroundColor: 'var(--danger)', 
      color: '#fff'
    },
    connectCard: {
      backgroundColor: 'var(--surface)',
      borderRadius: '12px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
      padding: '30px',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '50px auto'
    },
    connectIcon: {
      fontSize: '60px',
      color: 'var(--info)',
      marginBottom: '20px'
    },
    connectText: {
      fontSize: '18px',
      color: 'var(--text-muted)',
      marginBottom: '30px'
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '16px',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    primaryButton: {
      backgroundColor: 'var(--info)',
      color: '#fff'
    },
    primaryButtonHover: {
      backgroundColor: 'var(--info-hover)',
    },
    successButton: {
      backgroundColor: 'var(--success)',
      color: '#fff'
    },
    disabledButton: {
      backgroundColor: 'var(--text-muted)',
      color: 'var(--text)',
      cursor: 'not-allowed',
      opacity: '0.7'
    },
    mainContent: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '25px',
      marginBottom: '25px'
    },
    card: {
      backgroundColor: 'var(--surface)',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginTop: '0',
      marginBottom: '15px',
      color: '#2c3e50',
      borderBottom: '2px solid var(--surface2)',
      paddingBottom: '10px'
    },
    select: {
      width: '100%',
      padding: '12px 15px',
      borderRadius: '8px',
      border: '1px solid var(--border)',
      fontSize: '15px',
      backgroundColor: 'var(--surface2)',
      cursor: 'pointer',
      outline: 'none'
    },
    imagePreview: {
      padding: '15px',
      textAlign: 'center',
      backgroundColor: 'var(--surface2)',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    previewImg: {
      maxWidth: '100%',
      maxHeight: '180px',
      objectFit: 'contain',
      borderRadius: '8px',
      marginBottom: '10px'
    },
    noImage: {
      padding: '40px 20px',
      textAlign: 'center',
      color: 'var(--text-muted)',
      fontSize: '15px',
      backgroundColor: 'var(--surface2)',
      borderRadius: '8px',
      border: '1px dashed var(--border2)'
    },
    imageGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px',
      marginTop: '15px'
    },
    imageGridItem: {
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: '2px solid transparent'
    },
    imageGridItemSelected: {
      borderColor: 'var(--info)',
      boxShadow: '0 0 0 2px var(--info-dim)'
    },
    imageGridImage: {
      width: '100%',
      height: '80px',
      objectFit: 'cover',
      display: 'block'
    },
    imageName: {
      padding: '8px 5px',
      fontSize: '12px',
      backgroundColor: 'var(--surface2)',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    uploadButton: {
      display: 'block',
      width: '100%',
      marginTop: '20px'
    },
    progressContainer: {
      marginTop: '25px'
    },
    progressBar: {
      height: '10px',
      backgroundColor: 'var(--surface2)',
      borderRadius: '5px',
      overflow: 'hidden',
      marginBottom: '10px'
    },
    progressFill: {
      height: '100%',
      backgroundColor: 'var(--info)',
      borderRadius: '5px',
      transition: 'width 0.3s ease'
    },
    progressLabel: {
      textAlign: 'right',
      fontSize: '12px',
      color: '#64748b'
    },
    logSection: {
      marginTop: '25px'
    },
    logContainer: {
      height: '250px',
      overflowY: 'auto',
      backgroundColor: 'var(--surface)',
      color: 'var(--text)',
      padding: '15px',
      borderRadius: '8px',
      fontFamily: 'monospace',
      fontSize: '14px',
      lineHeight: '1.6',
      marginTop: '10px'
    },
    logEntry: {
      marginBottom: '8px',
      paddingBottom: '8px',
      borderBottom: '1px solid var(--border2)'
    },
    emptyLogs: {
      color: 'var(--text-muted)',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: '15px'
    },
    applyAllSection: {
      backgroundColor: 'var(--info-dim)',
      borderRadius: '12px',
      padding: '20px',
      marginTop: '20px',
      marginBottom: '25px',
      borderLeft: '5px solid var(--info)'
    },
    processingInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginTop: '15px'
    },
    fullWidthCard: {
      backgroundColor: 'var(--surface)',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
      gridColumn: '1 / span 2'
    },
    errorMessage: {
      backgroundColor: 'rgba(244,63,94,0.1)',
      color: 'var(--danger)',
      padding: '15px',
      borderRadius: '8px',
      borderLeft: '5px solid var(--danger)',
      marginBottom: '20px'
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      backgroundColor: 'var(--surface)',
      borderRadius: '12px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
    },
    spinner: {
        border: '5px solid var(--surface2)',
        borderTop: '5px solid var(--info)',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }
    };
  
    // If not connected, only show connect button
    if (!isConnected) {
      return (
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>DQR Images Publisher - ID: {id || 'N/A'}</h1>
            <div style={{...styles.statusBadge, ...styles.statusDisconnected}}>
              Disconnected
            </div>
          </header>
          
          <div style={styles.connectCard}>
            <div style={styles.connectIcon}>🔌</div>
            <p style={styles.connectText}>
              Please connect to your HID device to continue
            </p>
            <button 
              style={{...styles.button, ...styles.primaryButton}} 
              onClick={handleConnect}
              disabled={!('hid' in navigator)}
            >
              Connect HID Device
            </button>
            
            {!('hid' in navigator) && (
              <p style={{color: '#ef4444', marginTop: '15px', fontSize: '14px'}}>
                WebHID is not supported in this browser. Please try using Chrome.
              </p>
            )}
          </div>
        </div>
      );
    }
  
    // Show loading state
    if (loading) {
      return (
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>DQR Images Publisher - ID: {id || 'N/A'}</h1>
            <div style={{...styles.statusBadge, ...styles.statusConnected}}>
              Connected
            </div>
          </header>
          
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading DQR data...</p>
          </div>
        </div>
      );
    }
  
    // Show error state if any
    if (error) {
      return (
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>DQR Images Publisher - ID: {id || 'N/A'}</h1>
            <div style={{...styles.statusBadge, ...styles.statusConnected}}>
              Connected
            </div>
          </header>
          
          <div style={styles.errorMessage}>
            {error}
          </div>
        </div>
      );
    }
  
    // If processing all images, show progress view with all images and current image highlighted
    if (isProcessingAllImages) {
      // Find the current image being processed
      let currentImage = null;
      let currentScreenType = null;
      
      if (currentImageIndex > 0 && currentImageIndex <= Object.keys(screenTypes).length) {
        const option = Object.keys(screenTypes)[currentImageIndex - 1];
        const { category } = screenTypes[option];
        currentScreenType = screenTypes[option];
        
        if (dqrData?.data?.categorydata) {
          const matchingCategory = dqrData.data.categorydata.find(cat => 
            cat.categoryname.toUpperCase() === category
          );
          
          if (matchingCategory?.imagedata?.length > 0) {
            currentImage = matchingCategory.imagedata[0];
          }
        }
      }
      
      return (
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>DQR Images Publisher - ID: {id || 'N/A'}</h1>
            <div style={{...styles.statusBadge, ...styles.statusConnected}}>
              Connected
            </div>
          </header>
          
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Applying All Images</h2>
            
            <div style={{textAlign: 'center', margin: '20px 0'}}>
              <p style={{fontSize: '16px', marginBottom: '15px', fontWeight: '600'}}>
                Processing image {currentImageIndex} of {Object.keys(screenTypes).length}:
                {currentScreenType && ` ${currentScreenType.name} (${currentScreenType.category})`}
              </p>
              
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill, 
                    width: `${allImagesProgress}%`
                  }}
                ></div>
              </div>
              <div style={styles.progressLabel}>
                {allImagesProgress}% complete
              </div>
            </div>
            
            {/* Display current image being processed */}
            {currentImage && (
              <div style={{
                padding: '20px', 
                backgroundColor: 'var(--info-dim)', 
                borderRadius: '8px',
                marginTop: '20px',
                border: '1px solid var(--border)'
              }}>
                <h3 style={{...styles.cardTitle, color: 'var(--info)'}}>
                  Currently Processing
                </h3>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  <div style={{
                    width: '200px',
                    height: '150px',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <img 
                      src={currentImage.filepath} 
                      alt={currentImage.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                  
                  <div>
                    <h4 style={{margin: '0 0 10px 0', fontSize: '18px', color: 'var(--text)'}}>
                      {currentImage.name}
                    </h4>
                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: 'var(--text-muted)'}}>
                      {currentImage.filename}
                    </p>
                    
                    <div style={{marginTop: '15px'}}>
                      <div style={styles.progressBar}>
                        <div 
                          style={{
                            ...styles.progressFill, 
                            width: `${progress}%`,
                            backgroundColor: 'var(--info)'
                          }}
                        ></div>
                      </div>
                      <div style={styles.progressLabel}>
                        {progress}% complete
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Display all images that will be processed */}
            <div style={{marginTop: '25px'}}>
              <h3 style={styles.cardTitle}>All Images</h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '15px',
                marginTop: '15px'
              }}>
                {Object.keys(screenTypes).map((option, index) => {
                  const { category, name } = screenTypes[option];
                  let image = null;
                  
                  if (dqrData?.data?.categorydata) {
                    const matchingCategory = dqrData.data.categorydata.find(cat => 
                      cat.categoryname.toUpperCase() === category
                    );
                    
                    if (matchingCategory?.imagedata?.length > 0) {
                      image = matchingCategory.imagedata[0];
                    }
                  }
                  
                  return (
                    <div 
                      key={option}
                      style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: index + 1 === currentImageIndex 
                          ? '3px solid var(--info)' 
                          : index + 1 < currentImageIndex 
                            ? '3px solid var(--success)' 
                            : '1px solid var(--border)',
                        backgroundColor: 'var(--surface)'
                      }}
                    >
                      <div style={{
                        height: '100px',
                        backgroundColor: 'var(--surface2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {image ? (
                          <img 
                            src={image.filepath} 
                            alt={name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <div style={{
                            color: 'var(--text-muted)',
                            fontSize: '12px',
                            padding: '10px',
                            textAlign: 'center'
                          }}>
                            No image
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        padding: '10px',
                        backgroundColor: index + 1 === currentImageIndex ? 'var(--info-dim)' : 'var(--surface)'
                      }}>
                        <div style={{
                          fontWeight: index + 1 === currentImageIndex ? '600' : 'normal',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {name}
                        </div>
                        
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text-muted)',
                          marginTop: '4px',
                        }}>
                          {index + 1 < currentImageIndex ? (
                            <span style={{color: 'var(--success)'}}>✓ Completed</span>
                          ) : index + 1 === currentImageIndex ? (
                            <span style={{color: 'var(--info)'}}>⟳ Processing</span>
                          ) : (
                            <span>Pending</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            
             <button 
            style={{
              marginTop: "20px",
              background: "none",
              border: '1px solid var(--border)',
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              color: 'var(--text)',
              fontSize: "14px",
              transition: "all 0.2s"
            }}
            onClick={() => setShowLogs(!showLogs)}
          >
            {showLogs ? 'Hide Logs' : 'Show Logs'}
          </button>
          {showLogs && (
            <div 
              style={{
                marginTop: "20px",
                maxHeight: "150px",
                overflowY: "auto",
                textAlign: "left",
                fontSize: "13px",
                color: 'var(--text)',
                background: 'var(--surface2)',
                padding: "10px",
                borderRadius: "6px"
              }}
              ref={logContainerRef}
            >
              {logs.map((log, index) => (
                <div key={index}>{log}</div>
              ))}
            </div>
          )}
          </div>
        </div>
      );
    }
    
    // Main connected interface
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <h1 style={styles.title}>DQR Images Publisher - ID: {id || 'N/A'}</h1>
          <div style={{...styles.statusBadge, ...styles.statusConnected}}>
            Connected
          </div>
        </header>
        
        <div style={styles.applyAllSection}>
          <h2 style={styles.cardTitle}>Bulk Operations</h2>
          <button 
            onClick={handleApplyAllImages}
            disabled={!dqrData?.data?.categorydata}
            style={{
              ...styles.button, 
              ...styles.successButton,
              width: '100%'
            }}
          >
            Apply All Images
          </button>
        </div>
        
        <div style={styles.mainContent}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Screen Type Selection</h2>
            <select 
              value={selectedOption}
              onChange={handleOptionChange}
              style={styles.select}
            >
              {Object.keys(screenTypes).map(option => (
                <option key={option} value={option}>
                  {screenTypes[option].name}
                </option>
              ))}
            </select>
            
            {getCategoryImages().length > 1 && (
              <div style={{marginTop: '20px'}}>
                <h3 style={{...styles.cardTitle, fontSize: '16px'}}>
                  Select an Image ({selectedCategory?.categoryname})
                </h3>
                <div style={styles.imageGrid}>
                  {getCategoryImages().map((image) => (
                    <div 
                      key={image.id} 
                      style={{
                        ...styles.imageGridItem,
                        ...(imageToUpload?.id === image.id ? styles.imageGridItemSelected : {})
                      }}
                      onClick={() => {
                        setImageToUpload(image);
                        addLog(`Selected image: ${image.name}`);
                      }}
                    >
                      <img 
                        src={image.filepath} 
                        alt={image.name}
                        style={styles.imageGridImage}
                      />
                      <div style={styles.imageName}>
                        {image.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <button 
              onClick={handleUpload}
              disabled={!imageToUpload}
              style={{
                ...styles.button, 
                ...styles.primaryButton,
                ...styles.uploadButton,
                ...(imageToUpload ? {} : styles.disabledButton)
              }}
            >
              Upload Selected Image
            </button>
          </div>
          
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Image Preview</h2>
            {imageToUpload ? (
              <div style={styles.imagePreview}>
                <img 
                  src={imageToUpload.filepath} 
                  alt={imageToUpload.name}
                  style={styles.previewImg}
                />
                <div style={{fontWeight: '600'}}>{imageToUpload.name}</div>
                <div style={{fontSize: '13px', color: 'var(--text-muted)', marginTop: '5px'}}>
                  {imageToUpload.filename}
                </div>
              </div>
            ) : (
              <div style={styles.noImage}>
                No image selected for this screen type
              </div>
            )}
            
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill, 
                    width: `${progress}%`
                  }}
                ></div>
              </div>
              <div style={styles.progressLabel}>
                {progress}% complete
              </div>
            </div>
          </div>
  
          <button 
            style={{
              marginTop: "20px",
              background: "none",
              border: '1px solid var(--border)',
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              color: 'var(--text)',
              fontSize: "14px",
              transition: "all 0.2s"
            }}
            onClick={() => setShowLogs(!showLogs)}
          >
            {showLogs ? 'Hide Logs' : 'Show Logs'}
          </button>
          {showLogs && (
            <div 
              style={{
                marginTop: "20px",
                maxHeight: "150px",
                overflowY: "auto",
                textAlign: "left",
                fontSize: "13px",
                color: 'var(--text)',
                background: 'var(--surface2)',
                padding: "10px",
                borderRadius: "6px"
              }}
              ref={logContainerRef}
            >
              {logs.map((log, index) => (
                <div key={index}>{log}</div>
              ))}
            </div>
          )}
          
        </div>
      </div>
    );
  }
  export default PublishDQRImages;